import { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from '@/modules/Button';
import { Input } from '@/modules/Input';
import { Popup } from '@/modules/Popup';
import { EmojiPickerProps } from '@/shared/types/emojipicker';
import { EMOJI_CATEGORIES } from '../../constants/categories';
import { searchEmojis } from '../../constants/emojiKeywords';
import { cn } from '@/shared/utils/cn';
import { InnerButton } from '@/modules/InnerButton';
import { PopupRef } from '@/shared/types/popup';
import { faFaceSmile, faSearch } from '@fortawesome/pro-regular-svg-icons';

const STORAGE_KEY = 'emoji-picker-recent';

export const EmojiPicker = ({
  onEmojiSelect,
  maxRecentEmojis = 16,
  className = '',
  width = 400,
  height = 300,
  initialRecentEmojis = [],
  showSearch = true,
  searchPlaceholder = 'Buscar...',
  closeOnSelect = true,
  // Popup button props
  label = '',
  position = 'bottom-left',
  variant = 'icon',
  color = 'primary',
  size = 'sm',
  borderRadius = 'md',
  disabled = false,
  buttonClassName,
  tooltip,
}: EmojiPickerProps): JSX.Element => {
  const [activeCategory, setActiveCategory] = useState<string>('recent');
  const [searchQuery, setSearchQuery] = useState('');
  const [recentEmojis, setRecentEmojis] = useState<string[]>([]);
  const [selectedEmoji, setSelectedEmoji] = useState<string>(label || '');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const categoryRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const isInitialized = useRef(false);
  const popupRef = useRef<PopupRef | null>(null);

  // Load recent emojis from localStorage on mount
  useEffect(() => {
    if (!isInitialized.current) {
      isInitialized.current = true;

      if (initialRecentEmojis && initialRecentEmojis.length > 0) {
        setRecentEmojis(initialRecentEmojis);
      } else {
        try {
          const stored = localStorage.getItem(STORAGE_KEY);
          if (stored) {
            setRecentEmojis(JSON.parse(stored));
          }
        } catch (error) {
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.error('Error loading recent emojis:', error);
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- inicializar recientes solo al montar
  }, []);

  // Save recent emojis to localStorage
  const saveRecentEmojis = (emojis: string[]) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(emojis));
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('Error saving recent emojis:', error);
      }
    }
  };

  // Handle emoji selection
  const handleEmojiClick = (emoji: string) => {
    // Update recent emojis
    const updatedRecent = [emoji, ...recentEmojis.filter((e) => e !== emoji)].slice(
      0,
      maxRecentEmojis,
    );
    setRecentEmojis(updatedRecent);
    saveRecentEmojis(updatedRecent);

    // Update selected emoji
    // setSelectedEmoji(emoji)

    // Call callback
    onEmojiSelect?.(emoji);

    // Close popup if enabled
    if (closeOnSelect && popupRef.current) {
      popupRef.current.close();
    }
  };

  // Filter emojis based on search
  const filteredCategories = useMemo(() => {
    if (!searchQuery || searchQuery.trim() === '') {
      // Add recent emojis to the categories
      return [
        {
          ...EMOJI_CATEGORIES[0],
          emojis: recentEmojis,
        },
        ...EMOJI_CATEGORIES.slice(1),
      ];
    }

    // Filter emojis based on search using keywords
    const allCategories = EMOJI_CATEGORIES.slice(1).map((category) => ({
      ...category,
      emojis: searchEmojis(searchQuery, category.emojis),
    }));

    // Only return categories that have matching emojis
    const categoriesWithResults = allCategories.filter((category) => category.emojis.length > 0);

    return categoriesWithResults;
  }, [searchQuery, recentEmojis]);

  // Scroll to category
  const scrollToCategory = (categoryId: string) => {
    const categoryElement = categoryRefs.current[categoryId];
    if (categoryElement && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const offsetTop = categoryElement.offsetTop - container.offsetTop;
      container.scrollTo({
        top: offsetTop,
        behavior: 'smooth',
      });
    }
    setActiveCategory(categoryId);
  };

  // Handle scroll to update active category
  const handleScroll = () => {
    if (!scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const scrollTop = container.scrollTop;

    // Find which category is currently visible
    let currentCategory = 'recent';
    Object.entries(categoryRefs.current).forEach(([categoryId, element]) => {
      if (element) {
        const offsetTop = element.offsetTop - container.offsetTop;
        if (scrollTop >= offsetTop - 50) {
          currentCategory = categoryId;
        }
      }
    });

    setActiveCategory(currentCategory);
  };

  return (
    <Popup
      ref={popupRef}
      label={selectedEmoji}
      position={position}
      variant="button"
      color={color}
      size={size}
      icon={faFaceSmile}
      clickable={false}
      borderRadius={borderRadius}
      disabled={disabled}
      buttonClassName={buttonClassName}
      tooltip={tooltip}
      closeOnClick={false}
    >
      <div className={cn('ui-flex ui-flex-col ui-bg-fill', className)}>
        {/* Category Tabs */}
        <div className="ui-border-b ui-border-default ui-bg-surface ui-p-16">
          <div className="ui-flex ui-flex-wrap ui-justify-between ui-gap-8">
            {EMOJI_CATEGORIES.map((category) => (
              <Button
                key={category.id}
                borderable={activeCategory === category.id}
                size="xs"
                icon={category.icon}
                rounded={true}
                color={'tertiary'}
                pill="pill"
                onClick={() => scrollToCategory(category.id)}
                tooltip={category.name}
                tooltipPosition="bottom"
                aria-label={category.name}
              />
            ))}
          </div>
        </div>

        <div className="ui-flex ui-flex-col ui-gap-y-8 ui-p-16">
          {/* Search Input */}
          {showSearch && (
            <div className="ui-bg-surface">
              <Input
                leftSlot={faSearch}
                placeholder={searchPlaceholder}
                value={searchQuery}
                onValueChange={setSearchQuery}
                fullWidth
                size="sm"
                pill="sm"
              />
            </div>
          )}

          {/* Emoji Grid */}
          <div
            ref={scrollContainerRef}
            className="custom-scrollbar ui-overflow-y-auto"
            style={{ height: `${height}px` }}
            onScroll={handleScroll}
          >
            <div className="ui-flex ui-flex-col ui-gap-8">
              {filteredCategories.length === 0 ? (
                <div className="ui-py-8 ui-text-center ui-text-body-sm-regular ui-font-body-sm-regular ui-leading-body-sm-regular ui-tracking-body-sm ui-text-tertiary">
                  No se encontraron emojis
                </div>
              ) : (
                filteredCategories.map((category) => (
                  <div
                    key={category.id}
                    ref={(el) => {
                      categoryRefs.current[category.id] = el;
                    }}
                  >
                    {/* Category Title */}
                    <div className="ui-mb-2 ui-text-body-sm-regular ui-font-body-sm-regular ui-leading-body-sm-regular ui-tracking-body-sm ui-text-tertiary">
                      {category.name}
                    </div>

                    {/* Emoji Grid */}
                    <div className="ui-grid ui-grid-cols-8 ui-justify-items-center ui-gap-4 ui-pr-8">
                      {category.emojis.length === 0 && category.id === 'recent' ? (
                        <div className="ui-col-span-8 ui-py-4 ui-text-center ui-text-body-sm-regular ui-font-body-sm-regular ui-leading-body-sm-regular ui-tracking-body-sm ui-text-tertiary">
                          No hay emojis recientes
                        </div>
                      ) : (
                        category.emojis.map((emoji, index) => (
                          <InnerButton
                            key={`${category.id}-${emoji}-${index}`}
                            onClick={() => handleEmojiClick(emoji)}
                            aria-label={`Emoji ${emoji}`}
                            size="xs"
                            color="tertiary"
                            pill="pill"
                            icon={<span className="ui-text-base">{emoji}</span>}
                          />
                        ))
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </Popup>
  );
};
